import React, { useState, useEffect } from "react";
import { AdminWorkHistory } from "../../types/admin";
import { workHistoryApi } from "../../services/api";
import { useToast } from "./Toast";

const emptyWorkHistory: Omit<AdminWorkHistory, "id"> = {
  company: "",
  rolePt: "",
  roleEn: "",
  descriptionPt: "",
  descriptionEn: "",
  startDate: "",
  endDate: null,
};

const calculateDuration = (startDate: string, endDate: string | null): string => {
  if (!startDate) return "";

  const start = new Date(startDate + "-01");
  const end = endDate ? new Date(endDate + "-01") : new Date();

  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (months < 0) months = 0;

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  const parts: string[] = [];
  if (years > 0) parts.push(`${years} year${years > 1 ? "s" : ""}`);
  if (remainingMonths > 0) parts.push(`${remainingMonths} month${remainingMonths > 1 ? "s" : ""}`);

  return parts.length > 0 ? parts.join(", ") : "< 1 month";
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "";
  const [year, month] = dateStr.split("-");
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${months[parseInt(month) - 1]} ${year}`;
};

const WorkHistoryPage: React.FC = () => {
  const { showToast } = useToast();
  const [items, setItems] = useState<AdminWorkHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [form, setForm] = useState<Omit<AdminWorkHistory, "id">>(emptyWorkHistory);
  const [isCurrentJob, setIsCurrentJob] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await workHistoryApi.getAll();
      if (response.success && response.data) {
        setItems(response.data);
      } else {
        showToast(response.message || "Failed to load work history", "error");
      }
    } catch {
      showToast("Failed to load work history", "error");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyWorkHistory);
    setIsCurrentJob(false);
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = (item: AdminWorkHistory) => {
    setEditingId(item.id);
    setForm({
      company: item.company,
      rolePt: item.rolePt,
      roleEn: item.roleEn,
      descriptionPt: item.descriptionPt,
      descriptionEn: item.descriptionEn,
      startDate: item.startDate,
      endDate: item.endDate,
    });
    setIsCurrentJob(!item.endDate);
    setErrors({});
    setModalOpen(true);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.company.trim()) newErrors.company = "Company name is required";
    if (!form.rolePt.trim()) newErrors.rolePt = "Role (PT) is required";
    if (!form.roleEn.trim()) newErrors.roleEn = "Role (EN) is required";
    if (!form.startDate) newErrors.startDate = "Start date is required";
    if (!form.descriptionPt.trim()) newErrors.descriptionPt = "Description (PT) is required";
    if (!form.descriptionEn.trim()) newErrors.descriptionEn = "Description (EN) is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      ...form,
      endDate: isCurrentJob ? null : form.endDate,
    };

    setSaving(true);
    try {
      if (editingId) {
        const response = await workHistoryApi.update(editingId, payload);
        if (response.success) {
          showToast(response.message || "Work history updated successfully");
          setModalOpen(false);
          loadItems();
        } else {
          showToast(response.message || "Failed to update", "error");
        }
      } else {
        const response = await workHistoryApi.create(payload);
        if (response.success) {
          showToast(response.message || "Work history created successfully");
          setModalOpen(false);
          loadItems();
        } else {
          showToast(response.message || "Failed to create", "error");
        }
      }
    } catch {
      showToast("An error occurred", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string | number) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;

    try {
      const response = await workHistoryApi.delete(id);
      if (response.success) {
        showToast(response.message || "Entry deleted");
        loadItems();
      } else {
        showToast(response.message || "Failed to delete", "error");
      }
    } catch {
      showToast("An error occurred", "error");
    }
  };

  if (loading) {
    return (
      <div className="admin-loader">
        <div className="admin-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Work History</h1>
        <p className="admin-page-subtitle">
          Manage your professional experience timeline
        </p>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <span className="admin-card-title">
            Timeline ({items.length} {items.length === 1 ? "entry" : "entries"})
          </span>
          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={openCreate}>
            <i className="fa-solid fa-plus" />
            <span>Add Entry</span>
          </button>
        </div>

        {items.length === 0 ? (
          <div className="admin-empty-state">
            <i className="fa-solid fa-briefcase" />
            <p>No work history entries yet</p>
            <button className="admin-btn admin-btn-primary" onClick={openCreate}>
              <i className="fa-solid fa-plus" />
              <span>Add Your First Entry</span>
            </button>
          </div>
        ) : (
          <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Period</th>
                <th>Duration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div>
                      <div style={{ fontWeight: 600, color: "var(--color-text-primary)" }}>
                        {item.company}
                      </div>
                      <div style={{ fontSize: "1.2rem", color: "var(--color-text-muted)" }}>
                        {item.roleEn}
                      </div>
                    </div>
                  </td>
                  <td>
                    {formatDate(item.startDate)} — {item.endDate ? formatDate(item.endDate) : "Present"}
                  </td>
                  <td>
                    <span className="admin-duration-badge">
                      <i className="fa-solid fa-clock" />
                      {calculateDuration(item.startDate, item.endDate)}
                    </span>
                  </td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        onClick={() => openEdit(item)}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        className="admin-btn admin-btn-danger admin-btn-sm"
                        onClick={() => handleDelete(item.id)}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="admin-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">
                {editingId ? "Edit Entry" : "New Entry"}
              </h2>
              <button className="admin-modal-close" onClick={() => setModalOpen(false)}>
                &times;
              </button>
            </div>

            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label className="admin-form-label">Company</label>
                <input
                  type="text"
                  className={`admin-form-input${errors.company ? " admin-input-error" : ""}`}
                  placeholder="e.g. TETRIS IT / MAG Seguros"
                  value={form.company}
                  onChange={(e) => { setForm((prev) => ({ ...prev, company: e.target.value })); setErrors((prev) => ({ ...prev, company: "" })); }}
                />
                {errors.company && <span className="admin-field-error">{errors.company}</span>}
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label className="admin-form-label">Role (PT)</label>
                  <input
                    type="text"
                    className={`admin-form-input${errors.rolePt ? " admin-input-error" : ""}`}
                    placeholder="e.g. Desenvolvedor FullStack"
                    value={form.rolePt}
                    onChange={(e) => { setForm((prev) => ({ ...prev, rolePt: e.target.value })); setErrors((prev) => ({ ...prev, rolePt: "" })); }}
                  />
                  {errors.rolePt && <span className="admin-field-error">{errors.rolePt}</span>}
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Role (EN)</label>
                  <input
                    type="text"
                    className={`admin-form-input${errors.roleEn ? " admin-input-error" : ""}`}
                    placeholder="e.g. FullStack Developer"
                    value={form.roleEn}
                    onChange={(e) => { setForm((prev) => ({ ...prev, roleEn: e.target.value })); setErrors((prev) => ({ ...prev, roleEn: "" })); }}
                  />
                  {errors.roleEn && <span className="admin-field-error">{errors.roleEn}</span>}
                </div>
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label className="admin-form-label">Start Date</label>
                  <input
                    type="month"
                    className={`admin-form-input${errors.startDate ? " admin-input-error" : ""}`}
                    value={form.startDate}
                    onChange={(e) => {
                      setForm((prev) => ({ ...prev, startDate: e.target.value }));
                      setErrors((prev) => ({ ...prev, startDate: "" }));
                    }}
                  />
                  {errors.startDate && <span className="admin-field-error">{errors.startDate}</span>}
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">End Date</label>
                  <input
                    type="month"
                    className="admin-form-input"
                    value={form.endDate || ""}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, endDate: e.target.value || null }))
                    }
                    disabled={isCurrentJob}
                  />
                  <div className="admin-checkbox-group" style={{ marginTop: "0.8rem" }}>
                    <input
                      type="checkbox"
                      id="currentJob"
                      className="admin-checkbox"
                      checked={isCurrentJob}
                      onChange={(e) => {
                        setIsCurrentJob(e.target.checked);
                        if (e.target.checked) {
                          setForm((prev) => ({ ...prev, endDate: null }));
                        }
                      }}
                    />
                    <label htmlFor="currentJob" className="admin-checkbox-label">
                      I currently work here
                    </label>
                  </div>
                </div>
              </div>

              {form.startDate && (
                <div
                  style={{
                    marginBottom: "2rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.8rem",
                  }}
                >
                  <span className="admin-duration-badge">
                    <i className="fa-solid fa-clock" />
                    Duration: {calculateDuration(form.startDate, isCurrentJob ? null : form.endDate)}
                  </span>
                </div>
              )}

              <div className="admin-form-group">
                <label className="admin-form-label">Description (PT)</label>
                <textarea
                  className={`admin-form-textarea${errors.descriptionPt ? " admin-input-error" : ""}`}
                  placeholder="Description in Portuguese..."
                  value={form.descriptionPt}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, descriptionPt: e.target.value }));
                    setErrors((prev) => ({ ...prev, descriptionPt: "" }));
                  }}
                />
                {errors.descriptionPt && <span className="admin-field-error">{errors.descriptionPt}</span>}
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Description (EN)</label>
                <textarea
                  className={`admin-form-textarea${errors.descriptionEn ? " admin-input-error" : ""}`}
                  placeholder="Description in English..."
                  value={form.descriptionEn}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, descriptionEn: e.target.value }));
                    setErrors((prev) => ({ ...prev, descriptionEn: "" }));
                  }}
                />
                {errors.descriptionEn && <span className="admin-field-error">{errors.descriptionEn}</span>}
              </div>
            </div>

            <div className="admin-modal-footer">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-save"}`} />
                <span>{saving ? "Saving..." : editingId ? "Update" : "Create"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkHistoryPage;
